/**
 * LRU Object cache
 * Based on https://github.com/kibertoad/toad-cache/blob/main/src/LruObject.js
 */

interface LruItem<T> {
  expiry: number;
  key: string;
  prev: LruItem<T> | null;
  next: LruItem<T> | null;
  value: T;
}

export class LruObject<T> {
  private first: LruItem<T> | null;
  private items: { [key: string]: LruItem<T> };
  private last: LruItem<T> | null;
  private size: number;
  private readonly max: number;
  private readonly ttl: number;

  constructor({ max = 1000, ttlInMsecs = 0 }) {
    if (isNaN(max) || max < 0) {
      throw new Error('Invalid max value');
    }

    if (isNaN(ttlInMsecs) || ttlInMsecs < 0) {
      throw new Error('Invalid ttl value');
    }

    this.first = null;
    this.items = Object.create(null);
    this.last = null;
    this.size = 0;
    this.max = max;
    this.ttl = ttlInMsecs;
  }

  private bumpLru(item: LruItem<T>): void {
    if (this.last === item) {
      return; // Item is already the last one, no need to bump
    }

    const last = this.last;
    const next = item.next;
    const prev = item.prev;

    if (this.first === item) {
      this.first = next;
    }

    item.next = null;
    item.prev = last;
    if (last) {
      last.next = item;
    }

    if (prev !== null) {
      prev.next = next;
    }

    if (next !== null) {
      next.prev = prev;
    }

    this.last = item;
  }

  clear(): void {
    this.items = Object.create(null);
    this.first = null;
    this.last = null;
    this.size = 0;
  }

  delete(key: string): void {
    if (Object.prototype.hasOwnProperty.call(this.items, key)) {
      const item = this.items[key];

      delete this.items[key];
      this.size--;

      if (item.prev !== null) {
        item.prev.next = item.next;
      }

      if (item.next !== null) {
        item.next.prev = item.prev;
      }

      if (this.first === item) {
        this.first = item.next;
      }

      if (this.last === item) {
        this.last = item.prev;
      }
    }
  }

  deleteMany(keys: string[]): void {
    for (let i = 0; i < keys.length; i++) {
      this.delete(keys[i]);
    }
  }

  private evict(): void {
    if (this.size > 0) {
      const item = this.first;
      if (item) {
        delete this.items[item.key];

        if (--this.size === 0) {
          this.first = null;
          this.last = null;
        } else {
          this.first = item.next;
          // eslint-disable-next-line max-depth
          if (this.first) {
            this.first.prev = null;
          }
        }
      }
    }
  }

  expiresAt(key: string): number | undefined {
    if (Object.prototype.hasOwnProperty.call(this.items, key)) {
      return this.items[key].expiry;
    }
  }

  get(key: string): T | undefined {
    if (Object.prototype.hasOwnProperty.call(this.items, key)) {
      const item = this.items[key];

      // Item has already expired
      if (this.ttl > 0 && item.expiry <= Date.now()) {
        this.delete(key);
        return undefined;
      }

      // Item is still fresh
      this.bumpLru(item);
      return item.value;
    }
  }

  getMany(keys: string[]): (T | undefined)[] {
    const result: (T | undefined)[] = [];

    for (let i = 0; i < keys.length; i++) {
      result.push(this.get(keys[i]));
    }

    return result;
  }

  keys(): string[] {
    return Object.keys(this.items);
  }

  set(key: string, value: T): void {
    // Replace existing item
    if (Object.prototype.hasOwnProperty.call(this.items, key)) {
      const item = this.items[key];
      item.value = value;

      item.expiry = this.ttl > 0 ? Date.now() + this.ttl : this.ttl;

      if (this.last !== item) {
        this.bumpLru(item);
      }

      return;
    }

    // Add new item
    if (this.max > 0 && this.size === this.max) {
      this.evict();
    }

    const item: LruItem<T> = {
      expiry: this.ttl > 0 ? Date.now() + this.ttl : this.ttl,
      key: key,
      prev: this.last,
      next: null,
      value
    };
    this.items[key] = item;

    if (++this.size === 1) {
      this.first = item;
    } else if (this.last) {
      this.last.next = item;
    }

    this.last = item;
  }
}

// By creating the instance on the global scope it's availalbe through the application
// @ts-expect-error: `globalThis` cannot be extended inside a module file
const instance = (globalThis.lruCache = globalThis.lruCache || new LruObject({ max: 1000, ttlInMsecs: 1000 * 60 * 15 }));

export default instance;
